import api from './axios';
import { getActivePinia } from 'pinia';
import { useAuthStore } from '@/domains/auth/stores/auth.store';

let pendingCreateContext;
const pendingCreates = new Map();

export function isPrivateProviderContext() {
  return !!getActivePinia() && useAuthStore().currentOrgType === 'concierge';
}

// The existing forms expect a flat response. Keep the V2 boundary in one place.
export async function privateManagementRequest(method, path, payload, params) {
  const auth = useAuthStore();
  const org = auth.currentOrgId;
  const actor = auth.user?.user_id;
  const context = JSON.stringify([org, actor]);
  if (context !== pendingCreateContext) {
    pendingCreates.clear();
    pendingCreateContext = context;
  }
  let attempt;
  if (method.toLowerCase() === 'post') {
    const signature = JSON.stringify([payload, params]);
    attempt = pendingCreates.get(path);
    if (!attempt || attempt.signature !== signature) {
      attempt = { signature, key: crypto.randomUUID() };
      pendingCreates.set(path, attempt);
    }
  }
  // Keep the same key after an uncertain outcome; a changed draft is a new request.
  const response = await api.request({ method, url: `/api/v2/${path}`, data: payload, params,
    headers: attempt ? { 'Idempotency-Key': attempt.key } : undefined });
  if (org !== auth.currentOrgId || actor !== auth.user?.user_id) throw new Error('organization_changed');
  if (attempt && pendingCreates.get(path) === attempt) pendingCreates.delete(path);
  return { ...response, data: response.data.data };
}

export async function privateManagementList(resource, params = {}) {
  const auth = useAuthStore();
  const org = auth.currentOrgId;
  const actor = auth.user?.user_id;
  const items = [];
  let page = 1;
  let response;
  do {
    response = await api.get(`/api/v2/${resource}`, { params: { ...params, page, limit: 100 } });
    if (org !== auth.currentOrgId || actor !== auth.user?.user_id) throw new Error('organization_changed');
    items.push(...response.data.data);
    page += 1;
  } while (page <= response.data.meta.pagination.total_pages);
  return { ...response, data: items };
}

export function providerPayload(form, updating = false) {
  const keys = ['dni', 'name', 'primary_contact', 'second_contact', 'email', 'country', 'country_name',
    'location_one', 'location_two', 'location_three', 'state', 'description'];
  if (updating) keys.push('version');
  return Object.fromEntries(keys.filter(key => Object.prototype.hasOwnProperty.call(form, key)).map(key => [key, form[key]]));
}

export const providerFieldLabels = {
  name: 'provider.auto.nombre', email: 'provider.auto.correo', dni: 'provider.auto.identificacion',
  primary_contact: 'provider.auto.telefono_principal', second_contact: 'provider.auto.telefono_secundario',
  country: 'provider.auto.pais', country_name: 'provider.auto.pais',
  location_one: 'provider.auto.provincia', location_two: 'provider.auto.canton',
  commission_amount: 'provider.auto.comision', description: 'provider.auto.descripcion',
};

export function providerFormErrors(error, fallback, translate) {
  const body = error?.response?.data;
  const details = body?.variables?.details;
  const fields = {};
  if (Array.isArray(details)) {
    for (const detail of details) {
      if (typeof detail?.field === 'string' && typeof detail?.message === 'string') {
        const field = detail.field === 'country_name' ? 'country' : (Object.hasOwn(providerFieldLabels, detail.field) ? detail.field : 'form');
        if (fields[field]) continue;
        const kinds = { isNotEmpty: 'required', isDefined: 'required', isEmail: 'email',
          isPossiblePhoneNumber: 'phone', maxLength: 'too_long', isEnum: 'selection', isIn: 'selection' };
        const kind = kinds[detail.code] ?? 'invalid';
        const key = detail.field === 'description' && kind === 'required' ? 'description_required' : kind;
        fields[field] = translate
          ? (field === 'form' ? fallback : translate(`provider.validation.${key}`))
          : detail.message;
      }
    }
  } else if (Array.isArray(body?.message)) {
    for (const item of body.message) {
      if (!item || typeof item !== 'object') continue;
      for (const [field, value] of Object.entries(item)) {
        const message = typeof value === 'string' ? value : value?.constraints;
        const target = Object.hasOwn(providerFieldLabels, field) ? field : 'form';
        if (typeof message === 'string') fields[target] = message;
        else if (Array.isArray(message)) fields[target] = message.filter(value => typeof value === 'string').join('. ');
      }
    }
  }
  if (Object.keys(fields).length) return Object.entries(fields).map(([field, message]) => ({ [field]: message }));
  const message = body?.statusCode < 500 && typeof body?.message === 'string' ? body.message : fallback;
  return [{ form: message || fallback }];
}
